import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { TitleType } from '@/api/enum/common.enum';
import { useUpdateRuleDeleteMutation } from '@/api/task/ruleApiSlice';
import { TaskRuleDescriptionDTO } from '@/api/DTO/task/rule.interface';
import AppRichTextEditor from '@/common/components/editor/AppRichTextEditor';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { showLabel } from '@/utils/helper';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';

interface Props {
	type: FormType;
	setType: Dispatch<SetStateAction<FormType>>;
	detail?: TaskRuleDescriptionDTO;
}

export default function RuleInfoForm({ type, setType, detail }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [updateRuleDelete] = useUpdateRuleDeleteMutation();
	const [needConfirm, setNeedConfirm] = useState(false);
	const { ruleId } = useParams();
	const [currentTab, setCurrentTab] = useState('zh');
	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const defaultValues: DefaultValues<TaskRuleDescriptionDTO> = {
		descriptionZh: detail?.descriptionZh || '',
		descriptionEn: detail?.descriptionEn || '',
	};

	const formSchema = Yup.object().shape({
		descriptionZh: Yup.string().required(t('errorMessage.required')),
		limitPoints: Yup.string()
			.nullable()
			.when('type', {
				is: (type: string | number) => type == TitleType.DayIntegralDesc,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
	});
	const methods = useForm<TaskRuleDescriptionDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	useEffect(() => {
		console.log(detail);
		if (detail?.descriptionZh)
			methods.setValue('descriptionZh', detail.descriptionZh);
		if (detail?.descriptionEn)
			methods.setValue('descriptionEn', detail.descriptionEn);
		if (detail?.limitPoints)
			methods.setValue('limitPoints', detail.limitPoints);
	}, [detail]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<TaskRuleDescriptionDTO> = async (data) => {
		data.id = ruleId || '';
		const response = await updateRuleDelete(data);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changeContentSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			setType(FormType.view);
		}
	};

	const handleClose = () => {
		if (type === FormType.edit) {
			setType(FormType.view);
		} else {
			navigate('/task-manage/rule-list');
		}
	};
	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('ruleList.titleType')}
									value={detail?.titleZh}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('ruleList.updateTime')}
									value={
										!detail?.updatedAt
											? '-'
											: dayjs(detail?.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ padding: '24px 0' }}
						>
							<Typography variant='h6'>{t('meditation.text')}</Typography>
						</Box>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{detail?.type == TitleType.DayIntegralDesc && (
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='limitPoints'
										label={t('ruleList.IntegralUp')}
										inputType={InputType.number}
									/>
								</Grid>
							)}

							{currentTab == 'zh' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('ruleList.description')}
									</Typography>
									<AppRichTextEditor
										editorTheme='style2'
										control={methods.control}
										name='descriptionZh'
									/>
								</Grid>
							)}
							{currentTab == 'en' && (
								<Grid item xs={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('ruleList.description')}
									</Typography>
									<AppRichTextEditor
										editorTheme='style2'
										control={methods.control}
										name='descriptionEn'
									/>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
